/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReportDelayProvider, ReportProvider, ErrorProvider } from '../../../providers';
import { ReportService } from '../../../services/report.service';
import { Report, User, Stage, State, Helpers, HistoryStage, ReportUserType, ReportUser } from '../../../model';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { StageService, UserService, UserPersistenceService, ModeService ,LogService } from '../../../services';
import {
  ReportStageModal, ReportDelayModal,
  ConfirmCancelReportModal, NotificationEngagedModal, ConfirmRevertModal, ReportMergeModal
} from './modals';
import { ReportNotification } from '../../../model/report.notification';
import * as moment from 'moment';
import { DuplicateReportModal } from '../../modal';
import { SystemType } from '../../../model/enums/system.type';
import * as _ from 'lodash';
import { ConfirmDeleteReportModal } from '../dashboard/modal/confirm-delete/confirm-delete-report.modal';
import { BVQReportProvider } from 'src/app/providers/bvq';
import { BVQReportService, ReportSourceService } from 'src/app/modules/bvq/services';
import { BVQReport } from 'src/app/model/bvq';

@Component({
  selector: 'page-report',
  templateUrl: 'report.page.html',
  styleUrls: ['report.page.scss']
})
export class ReportPage implements OnInit, OnDestroy {
  currentReport: Report;
  stages: Stage[];

  private reportSub: any;
  private stagesSub: any;
  private modSub: any;
  private reportNotificationSub: any;

  changeStageModal: BsModalRef;
  delayModal: BsModalRef;
  cancelModal: BsModalRef;
  engagedModal: BsModalRef;
  reverModal: BsModalRef;
  unlockModal: BsModalRef;
  mergeModal: BsModalRef;
  quoteRef: BsModalRef;
  deleteModal: BsModalRef;
  isLocked = true;

  tempUsers = new Map();
  duplicateReportModal: BsModalRef;
  SystemType = SystemType;
  currentMode: SystemType;
  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reportProvider: ReportProvider,
    private bvqReportProvider: BVQReportProvider,
    private reportService: ReportService,
    private modalService: BsModalService,
    private stageService: StageService,
    private userService: UserService,
    private reportDelayProvider: ReportDelayProvider,
    private userPersistenceService: UserPersistenceService,
    private errorProvider: ErrorProvider,
    private modeService: ModeService,
    private bvqProvider: BVQReportProvider,
    private sourceService: ReportSourceService,
    private bvqReportService: BVQReportService,
    private logService : LogService
  ) { }

  ngOnInit(): void {
    this.modSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.reportSub = this.reportService.report$.subscribe(currentReport => this.currentReport = currentReport);
    this.stagesSub = this.reportService.reportStages$.subscribe(stages => this.stages = stages);
    this.reportNotificationSub = this.reportService.reportNotifications$.subscribe(n => this.handleNotification(n));

    this.route.params.subscribe((params: Params) => {
      if (params['id']) {
        if (history.state && history.state.report) {
          const report: Report = this.userPersistenceService.persistReportUsers(history.state.report);
          this.reportService.setReport(report);
          this.reportService.setReportLock(true);
        }
        else {
          this.loading = true;
          this.reportProvider.getReportById(params['id'])
            .subscribe(
              data => {
                if (data.success) {
                  const report: Report = Report.parse(data.result);
                  this.reportService.setReport(this.userPersistenceService.persistReportUsers(report));
                  this.reportService.setReportLock(true);

                  if (report.TYPE === SystemType.EA)
                    this.modeService.mode.next(SystemType.EA);
                  else if (report.TYPE === SystemType.BV)
                    this.modeService.mode.next(SystemType.BV);

                  if (report.HAS_BVQ) {
                    this.bvqReportProvider.getReportById(params['id']).subscribe(
                      data => {
                        if (data.success) {
                          let report = this.userPersistenceService.persistReportUsers(data.result);
                          this.bvqReportService.setReport(new BVQReport(report.BVQ));

                        }
                        this.loading = false;
                      }, err => {
                        // TODO: Handle Errors
                        this.logService.error(err);
                        this.loading = false;
                      });
                  }



                }
                this.loading = false;
              });


        }
      } else this.router.navigate(['/']);
    });

  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
    if (this.reportNotificationSub) this.reportNotificationSub.unsubscribe();
    if (this.stagesSub) this.stagesSub.unsubscribe();
    if (this.modSub) this.modSub.unsubscribe();
    // Clear current Report
    this.reportService.clearReport();
  }

  changeStage(): void {

    this.changeStageModal = this.modalService.show(ReportStageModal, Helpers.getModalConfig());
    const ackSubject = new Subject<HistoryStage>();
    this.changeStageModal.content.subject = ackSubject;
    this.changeStageModal.content.reportId = this.currentReport.ID;
    this.changeStageModal.content.nextStageId = this.stageService.getNextStageId(this.currentReport.STAGE.ID);
    this.changeStageModal.content.userList = this.currentReport.USERS;
    this.changeStageModal.content.currentReport = this.currentReport;
    this.changeStageModal.content.setReportUsers();
    this.changeStageModal.content.getReportUser();
    this.changeStageModal.content.setStageList();

    ackSubject.subscribe(value => {
      if (value) {
        this.reportService.addStageHistory(value);
        this.currentReport.STAGE = this.stageService.getStageById(value.SID);
        this.currentReport.ASSIGNEE = value.ASSIGNEE;
        this.currentReport.STATE = value.STATE;

        if (value.FILE_LOCATION)
          this.currentReport.FILE_LOCATION = value.FILE_LOCATION;

        if (value.DELAY) {
          this.currentReport.DELAYS.push(value.DELAY);
        }

        if (value.SID === 'ENGD' || value.SID === 'EAED') {
          this.currentReport.DATE_ENGAGED = moment(value.DATE_ENGAGED).format('MMM DD, YYYY');
          this.reportService.updateDateEngaged.emit();
          this.currentReport.HAS_BVQ = true;
        }

        if (value.SID === 'ELST') {
          this.refreshReport();
        }

        switch (value.SID) {
          case 'INDI':
          case 'QUOT':
          case 'ORDR':
          case 'FBRI':
          case 'ELST':
          case 'ENGD':
            if (this.currentReport.USERS[ReportUserType.PROCESSOR.toString()] == null) {
              const reportUser: ReportUser = new ReportUser();
              reportUser.USER = User.parse(value.ASSIGNEE);
              this.currentReport.getUsers()[ReportUserType.PROCESSOR.toString()] = reportUser;
            }
            break;
          case 'STRT':
            if (this.currentReport.USERS[ReportUserType.RESEARCH_ANALYST.toString()] == null) {
              const reportUser: ReportUser = new ReportUser();
              reportUser.USER = User.parse(value.ASSIGNEE);
              this.currentReport.getUsers()[ReportUserType.RESEARCH_ANALYST.toString()] = reportUser;
            }
            break;
          case 'SPST':
          case 'SPRE':
          case 'R1DR':
          case 'RDY2':
            if (this.currentReport.USERS[ReportUserType.FINANCIAL_ANALYST.toString()] == null) {
              const reportUser: ReportUser = new ReportUser();
              reportUser.USER = User.parse(value.ASSIGNEE);
              this.currentReport.getUsers()[ReportUserType.FINANCIAL_ANALYST.toString()] = reportUser;
            }
            break;
          case 'REVW':
          case 'REV2':
            if (this.currentReport.USERS[ReportUserType.SENIOR_ANALYST.toString()] == null) {
              const reportUser: ReportUser = new ReportUser();
              reportUser.USER = User.parse(value.ASSIGNEE);
              this.currentReport.getUsers()[ReportUserType.SENIOR_ANALYST.toString()] = reportUser;
            }
            break;
          case 'RDY1':
          case 'VLCK':
            // Senior Reviewer - Rohul Patel
            break;
          case 'FINR':
            // Senior Appraiser - Neal Patel
            break;
        }
      }
    });
  }

  delayModalAction(): void {
    this.delayModal = this.modalService.show(ReportDelayModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.delayModal.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.logService.log(value);
      }
    });
  }

  handleNotification(n: ReportNotification): void {
    if (n && this.currentReport && n.ID === this.currentReport.ID) {
      this.engagedModal = this.modalService.show(NotificationEngagedModal, Helpers.getModalConfig());
      const ackSubject = new Subject<any>();
      this.engagedModal.content.subject = ackSubject;

      ackSubject.subscribe(() => {
        this.refreshReport();
      });
    }
  }

  refreshReport(): void {
    this.reportProvider.getReportById(this.currentReport.ID)
      .subscribe(
        data => {
          if (data.success)
            this.reportService.setReport(Report.parse(data.result));
          else {
            // TODO: Handle Errors
            this.logService.error(data);
          }
        }, err => {
          // TODO: Handle Errors
          this.logService.error(err);
        });
  }

  cancelReport(): void {
    this.cancelModal = this.modalService.show(ConfirmCancelReportModal, Helpers.getModalConfig());
    const ackSubject = new Subject<any>();
    this.cancelModal.content.subject = ackSubject;

    ackSubject.subscribe(val => {
      if (val.VALUE) {
        this.reportProvider.cancelReport(this.currentReport.ID, val.REASON).subscribe(data => {
          if (data.success) {
            this.currentReport.STATE = State.parse(data.result);
          }
        });
      }
    });
  }

  revertReport(): void {
    this.reverModal = this.modalService.show(ConfirmRevertModal, Helpers.getModalConfig());
    const ackSubject = new Subject<Boolean>();
    this.reverModal.content.subject = ackSubject;

    ackSubject.subscribe(result => {
      if (result) {
        this.reportProvider.revertReport(this.currentReport.ID)
          .subscribe(data => {
            if (data.success) {
              const historyStage = HistoryStage.parse(data.result);

              if (historyStage) {
                if (this.currentReport.HISTORY) {
                  const historyIndex = this.currentReport.HISTORY.findIndex(h => h.ID === historyStage.HID);
                  this.currentReport.HISTORY.splice(historyIndex, 1);
                }

                this.stages.map(s => {
                  if (s.ID === this.currentReport.STAGE.ID) {
                    if (s.hasTransitions) {
                      s.HISTORY = [];
                    }
                  }
                  return s;
                });

                this.reportService.reportStages.next(this.stages);

                const stage = this.stageService.getStageById(historyStage.SID);

                this.currentReport.STAGE = stage;

                if (historyStage.STATE)
                  this.currentReport.STATE = historyStage.STATE;

                if (historyStage.ASSIGNEE) {
                  this.currentReport.ASSIGNEE = historyStage.ASSIGNEE;
                }
              }
            }
          });
      }
    });
  }

  onLockChange(): void {
    /*let message: string;

    if (!this.userService.isAdmin() || !this.isEngaged) {
      if (!this.userService.isAdmin())
        message = 'You don’t have enough privileges to unlock this report.';
      else if (!this.isEngaged)
        message = 'Report is not at the stage that requires unlocking.';

      this.unlockModal = this.modalService.show(UnlockReportInfoModal);
      const ackSubject = new Subject<Boolean>();
      this.unlockModal.content.subject = ackSubject;
      this.unlockModal.content.message = message;

      ackSubject.subscribe(result => {
        if (result) {

        }
      });
    } else {*/
    this.isLocked = !this.isLocked;
    this.reportService.setReportLock(this.isLocked);
    // }
  }

  get isEngaged(): boolean {
    return !!this.currentReport.DOCUMENTS.length || this.currentReport.STAGE.ORDER >= 5;
  }

  onMerge(): void {
    this.mergeModal = this.modalService.show(ReportMergeModal, Helpers.getModalConfig());
    const ackSubject = new Subject<Report>();
    this.mergeModal.content.subject = ackSubject;
    this.mergeModal.content.report = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        setTimeout(() => {
          this.router.navigate(['/report', value.ID, 'general']);
        }, 2000);
      }
    });
  }

  gotoParent(): void {
    this.router.navigate(['/report', this.currentReport.PARENT_ID, 'general']);
  }

  gotoOriginalReport(): void {
    this.router.navigate(['/report', this.currentReport.DUPLICATE_PARENT_ID, 'general']);
  }

  onDuplicate(report: Report): void {
    let config = Helpers.getModalConfig();
    config['report'] = _.cloneDeep(report);
    this.duplicateReportModal = this.modalService.show(DuplicateReportModal, config);
    const ackSubject = new Subject<any>();
    this.duplicateReportModal.content.subject = ackSubject;
    ackSubject.subscribe(value => {
      if (value.ACCEPT) {
        this.reportProvider.duplicateReport(report.ID, value.ENTITIES, value.INTERNAL_FILE_NAME)
          .subscribe(
            data => {
              if (data.success) {
                this.reportService.setReport(Report.parse(data.result));

                if (this.currentMode === SystemType.BV)
                  this.router.navigate(['/report', data.result.ID, 'general']);
                else if (this.currentMode === SystemType.EA)
                  this.router.navigate(['/report-ea', data.result.ID, 'general']);
              }
            });
      }
    });
  }

  onQuote(): void {
    this.router.navigate(['/processing/quote', this.currentReport.ID, 'general'],
      { state: { report: this.currentReport } });
  }

  deleteReport(): void {
    this.deleteModal = this.modalService.show(ConfirmDeleteReportModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.deleteModal.content.subject = ackSubject;
    this.deleteModal.content.report = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.deleteReport(this.currentReport.ID)
          .subscribe(
            data => {
              if (data.success) {
                this.router.navigate(['/dashboard']);
              }
            });
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActivate(event): void {
    window.scroll(0, 0);
  }

  viewBVQ(): void {
    window.open(`https://app.reliantvalue.com/reports/${this.currentReport.ID}/overview/general`, '_blank');
  }

  /** Switch to BVQ mode */
  switchToBVQ(): void {
    this.loading = true;
    this.bvqProvider.getReportById(this.currentReport.ID)
      .subscribe(data => {
        if (data.success) {
          this.loading = false;
          this.sourceService.setSource(null);
          this.router.navigate(['/bvq/report', this.currentReport.ID, 'overview'],
            { state: { report: data.result } });
        }
      }, err => {
        this.logService.error(err);
        this.loading = false;
      });

    //this.router.navigate(['/bvq/report', this.currentReport.ID, 'overview']);
  }

  /** Create BVQ */
  createBVQ(): void {
    this.bvqProvider.createReport(this.currentReport.ID)
      .subscribe(data => {
        if (data.success) {
          this.router.navigate(['/bvq/report', this.currentReport.ID, 'overview']);
        }
      });
  }
}
